<template>
  <article class="activity-info w-[1335px] mx-auto h-full flex flex-col">
    <a-breadcrumb class="mt-[30px] mb-[20px]" :separator="separator">
      <a-breadcrumb-item class="text-font-60">
        <nuxt-link v-if="isFromAcademy" to="/academy">
          {{ $t('mo-fa-xue-yuan') }}
        </nuxt-link>
        <nuxt-link v-else to="/platform">
          {{ $t('shou-ye') }}
        </nuxt-link>
      </a-breadcrumb-item>
      <a-breadcrumb-item>
        <nuxt-link to="/tutorial">{{ $t('jiao-cheng') }}</nuxt-link>
      </a-breadcrumb-item>
      <a-breadcrumb-item>{{ data?.title }}</a-breadcrumb-item>
    </a-breadcrumb>
    <main class="flex gap-[24px] flex-1 relative">
      <div class="tutorial-detail mr-[356px] flex-1">
        <section
          class="w-full h-[552px] rounded-[10px] bg-[#2B2B2E] mr-[20px] overflow-hidden"
        >
          <VideoPlayer
            v-if="data?.video_m3u8_url || data?.video_url"
            :id="activityId"
            :poster="transformImage(data?.cover_url)"
            :url="data?.video_url"
            :auto-url="data?.video_m3u8_url"
            type="template"
            fetchpriority="high"
          />
        </section>
        <header
          class="template-title flex-y-center mt-[20px] flex justify-between"
        >
          <div>
            <h1 class="text-[20px] font-semibold my-0">
              {{ data?.title }}
            </h1>
            <div class="text-font-40 text-[12px] mt-[12px]">
              {{ timeFormat(data?.create_time) }}
            </div>
          </div>
          <a-popover :content="$t('dian-ji-fu-zhi-fen-xiang-lian-jie')">
            <a-button
              type="default"
              class="flex-y-center gap-[5px] rounded-[18px] h-[36px] min-w-[76px]"
              @click="showShare"
            >
              <iconpark-icon name="arrow-share"></iconpark-icon>
              {{ $t('fen-xiang') }}
            </a-button>
          </a-popover>
        </header>
        <!-- <section
          class="template-desc mt-[12px] flex-y-center gap-[10px] text-font-40 text-12"
        >
          <span class="flex-y-center gap-[4px]" :title="avatarName">
            <img loading="lazy" class="avatar" :src="avatarUrl" />
            {{ avatarName }}
          </span>
        </section> -->
        <section
          v-if="data?.description"
          class="text-14 text-font-80 leading-[28px] mt-[16px]"
        >
          {{ data?.description }}
        </section>
      </div>
      <div
        class="absolute top-0 right-0 w-[332px] h-full overflow-auto pb-[7px]"
      >
        <section
          v-if="otherList?.length"
          class="rounded-[10px] h-full flex flex-col"
        >
          <div
            class="font-bold text-[14px] text-font-80 flex-y-center gap-[6px]"
          >
            <iconpark-icon name="book-title" size="16"></iconpark-icon>
            {{
              data?.guide_video_class_id
                ? $t('tong-xi-lie-jiao-cheng')
                : $t('qi-ta-jiao-cheng')
            }}
          </div>
          <div
            class="overflow-auto flex-1 flex flex-col gap-[16px] pr-[18px] mt-[16px]"
          >
            <OtherCard v-for="item in otherList" :key="item.id" :data="item" />
          </div>
        </section>
      </div>
    </main>
  </article>
</template>
<script setup lang="ts" name="Activity">
import OtherCard from './components/otherCard.vue'
import { fetchGuidVideoSeries } from '~/api'
import type { Template } from '~/api/interface'
import { getOtherList } from '~/features/class/components/template'
import { useTime } from '~/hooks/useTime'
import { useWatchPageSeoData } from '~/hooks/useSeo'
import { PageKey } from '~/constants/page'

const route = useRoute()
const activityId = computed(() => Number(route.params.id))
const separator = h('iconpark-icon', { name: 'breadcrumb-separator', size: 10 })
const { timeFormat } = useTime()
const domHeight = ref<any>(660)
const { $t } = useNuxtApp()

const isFromAcademy = computed(() => route.query.nav === PageKey.ACADEMY)

const { data: dataList } = useAsyncData(
  async () => {
    const res = await fetchGuidVideoSeries(activityId.value)

    return res.data?.map((item) => ({
      ...item,
      name: item.title,
      vertical: true,
      type: CARD_TYPE.MAIN,
      '3DTag': false,
    }))
  },
  { default: () => [] }
)

const data = computed(() => {
  return dataList.value?.find((o) => o.id === activityId.value) as unknown as
    | Template
    | undefined
})

const seoData = computed(() => {
  return {
    seo_title: data.value?.title,
    seo_description: data.value?.description,
  }
})

useWatchPageSeoData(seoData)

const otherList = computed(() =>
  data?.value?.guide_video_class_id
    ? dataList.value?.filter((o) => o.id !== data?.value?.id) // 系列视频
    : getOtherList(dataList.value!, data.value)
)

const showShare = () => {
  copyToClipboard(location.href)
  message.success($t('fu-zhi-cheng-gong'))
}

provideResourceLocation({
  source: SOURCE.Tutorial,
})

watch(
  () => data.value,
  (val) => {
    if (val && import.meta.client) {
      trackEvent(TRACK_EVENT.PREVIEW_EXCELLENT_WORK, {
        template_id: val.id,
        template_title: val.title,
      })
    }
  }
)

onMounted(() => {
  nextTick(() => {
    domHeight.value =
      document.querySelector('.tutorial-detail')!.clientHeight - 100
  })
})
</script>

<style lang="scss" scoped>
.avatar {
  @apply w-[16px] h-[16px] rounded-[50%];
  border: 1px solid var(--back-3);
}

.activity-info {
  :deep(.card-container) {
    height: 200px;
  }

  :deep(.card-item .title) {
    margin-top: unset;
  }
}
</style>
